import React, { useState, forwardRef, useRef } from "react";
import { Box, Typography, useTheme, IconButton } from "@mui/material";
import { tokens } from "../../theme";
import ArrowDirectionIcon from "../../components/ArrowDirectionIcon";
import PlayerContextMenu from "../../components/PlayerContextMenu";
import PlayerAvatar from "./PlayerAvatar";
import { useSettings } from "../../contexts/SettingsContext";
import FavoriteIcon from "@mui/icons-material/Favorite";
import LockPersonIcon from "@mui/icons-material/LockPerson";

const DraftGridCell = forwardRef(
  (
    {
      playerData,
      round,
      draft_position,
      draftedIds,
      manuallyDraftedIds = new Set(), // Add manually drafted IDs prop
      scoringType,
      onDraftToggle,
      tierCounts, // Add tier counts prop for scarcity calculation
      positionColors, // Add position colors as a prop
      teamCount = 12, // Add teamCount prop with default value
      highlightedPlayer = "", // Add highlighted player prop
    },
    ref
  ) => {
    const [contextMenuAnchor, setContextMenuAnchor] = useState(null);
    const theme = useTheme();
    const colors = tokens(theme.palette.mode);
    const { favoritePlayerIds, toggleFavoritePlayer } = useSettings();
    // Refs must be declared before any early returns
    const longPressTimerRef = useRef(null);
    const longPressTriggeredRef = useRef(false);

    if (!playerData) {
      return (
        <Box
          ref={ref}
          sx={{
            backgroundColor: colors.primary[400],
            borderRadius: "4px",
            height: {
              xs: "60px",
              sm: "70px",
              md: "80px",
              lg: "80px",
              xl: "80px",
            },
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Typography color={colors.grey[500]} variant="body2">
            Empty
          </Typography>
        </Box>
      );
    }

    const {
      id,
      sleeper_id = "",
      name = "",
      team = "",
      position = "",
      bye_week = 0,
      ovr_rank_ecr,
      pos_rank_ecr: pos_rank = "",
      // pos_tier,
      adp_rank = "",
    } = playerData;

    // Determine if player is drafted by checking multiple sources
    const isDrafted =
      playerData.isDrafted ||
      draftedIds.includes(sleeper_id) ||
      manuallyDraftedIds.has(sleeper_id);

    // Fallback colors if positionColors prop is not provided (Sleeper light mode colors)
    const defaultPositionColors = {
      RB: "rgba(143, 242, 202, 0.8)",
      WR: "rgba(86, 201, 248, 0.8)",
      QB: "rgba(239, 116, 161, 0.8)",
      TE: "rgba(254, 174, 88, 0.8)",
      K: "rgba(182, 185, 255, 0.8)",
      DEF: "rgba(191, 117, 93, 0.8)",
    };

    const activePositionColors = positionColors || defaultPositionColors;
    const originalColor = activePositionColors[position] || colors.primary[500];
    const bgColor = isDrafted ? colors.grey[900] : originalColor;
    const textColor = isDrafted ? originalColor : colors.primary[500];
    const mutedGray = colors.grey[400];

    // Tier styling logic not currently used in UI

    const handleClick = () => {
      if (longPressTriggeredRef.current) {
        // Suppress click if this was a long-press
        longPressTriggeredRef.current = false;
        return;
      }
      onDraftToggle?.();
    };

    const handleRightClick = (event) => {
      event.preventDefault();
      setContextMenuAnchor(event.currentTarget);
    };

    const handleCloseContextMenu = () => {
      setContextMenuAnchor(null);
    };

    const clearLongPressTimer = () => {
      if (longPressTimerRef.current) {
        clearTimeout(longPressTimerRef.current);
        longPressTimerRef.current = null;
      }
    };

    const handleTouchStart = (event) => {
      const targetEl = event.currentTarget;
      longPressTriggeredRef.current = false;
      clearLongPressTimer();
      longPressTimerRef.current = setTimeout(() => {
        longPressTriggeredRef.current = true;
        setContextMenuAnchor(targetEl);
      }, 750);
    };

    const handleTouchEnd = (event) => {
      if (longPressTimerRef.current) {
        // Short touch: let onClick handle drafting
        clearLongPressTimer();
        return;
      }
      if (longPressTriggeredRef.current) {
        // Long press already opened menu; prevent click
        event.preventDefault();
        event.stopPropagation();
      }
    };

    // Format the player name to show first initial and last name
    const formatPlayerName = (fullName) => {
      if (!fullName) return "";
      const nameParts = fullName.split(" ");
      if (nameParts.length < 2) return fullName;
      return `${nameParts[0].charAt(0)}. ${nameParts.slice(1).join(" ")}`;
    };

    // Extract first and last name for PlayerAvatar
    const getPlayerNames = (fullName) => {
      if (!fullName) return { first_name: "", last_name: "" };
      const nameParts = fullName.split(" ");
      if (nameParts.length < 2) return { first_name: fullName, last_name: "" };
      return {
        first_name: nameParts[0],
        last_name: nameParts.slice(1).join(" ")
      };
    };

    const { first_name, last_name } = getPlayerNames(name);

    // Compare ECR and ADP for highlighting
    const getComparisonStyles = (ecr, adp) => {
      if (!ecr || !adp || adp === 999) return { ecrStyle: {}, adpStyle: {} };

      const ecrNum = parseInt(ecr);
      const adpNum = parseInt(adp);
      const differenceEcrAdp = Math.abs(ecrNum - adpNum);

      const baseStyle = {
        backgroundColor: colors.primary[900],
        borderRadius: "4px",
        paddingTop: "2px",
        padding: "0 4px",
      };

      // Only highlight if difference is 5 or more
      if (differenceEcrAdp < 5) return { ecrStyle: {}, adpStyle: {} };

      return {
        ecrStyle:
          ecrNum < adpNum
            ? {
                ...baseStyle,
                color: colors.greenAccent[300],
                fontWeight: "bold",
              }
            : {},
        adpStyle:
          adpNum < ecrNum
            ? {
                ...baseStyle,
                color: colors.redAccent[300],
                fontWeight: "bold",
              }
            : {},
      };
    };

    const { ecrStyle, adpStyle } = getComparisonStyles(ovr_rank_ecr, adp_rank);

    const handleFavoriteClick = (e) => {
      e.stopPropagation(); // Prevent cell click
      toggleFavoritePlayer(sleeper_id || id);
    };

    // Players are keepers if they are custom assigned, not just manually drafted
    // customDraftAssignments would need to be passed as a prop to determine this properly
    // For now, we'll need to check if this player is in a custom assignment
    const isKeeper = playerData.isCustomAssigned || false;

    return (
      <>
          <Box
            ref={ref}
            onClick={handleClick}
            onContextMenu={handleRightClick}
            onTouchStart={handleTouchStart}
            onTouchEnd={handleTouchEnd}
            onTouchCancel={clearLongPressTimer}
            sx={{
              backgroundColor: bgColor,
              borderRadius: "3px",
              padding: {
                xs: "2px 3px 3px",
                sm: "2px 4px 4px", 
                md: "2px 4px 4px"
              },
              cursor: "pointer",
              transition: "all 0.2s ease",
              border: favoritePlayerIds.includes(sleeper_id || id)
                ? `2px solid ${colors.redAccent[500]}`
                : highlightedPlayer &&
                  name.toLowerCase().includes(highlightedPlayer.toLowerCase())
                ? `2px solid ${colors.yellowAccent[500]}`
                : "none",
              display: "flex",
              flexDirection: "column",
              justifyContent: "flex-start", // Change from space-between to flex-start
              gap: {
                xs: "0px",
                sm: "1px", 
                md: "1px",
                lg: "1px",
                xl: "1px",
              },
              height: {
                xs: "68px",
                sm: "72px",
                md: "76px",
                lg: "76px",
                xl: "76px",
              },
              "&:hover": {
                filter: "brightness(1.1)",
                transform: "scale(1.02)",
              },
              position: "relative",
            }}
          >
            {/* Top-left: heart and keeper indicator */}
            <Box
              display="flex"
              alignItems="center"
              gap={0.2}
              mb={0.2}
              mt="-1px"
              height="16px"
            >
                <IconButton 
                  size="small" 
                  onClick={handleFavoriteClick}
                  sx={{
                    padding: 0,
                    minWidth: "14px",
                    minHeight: "14px",
                    "& svg": {
                      fontSize: "12px",
                      filter: isDrafted
                        ? `drop-shadow(0px 0px 1px ${colors.grey[900]})`
                        : favoritePlayerIds.includes(sleeper_id || id)
                        ? `drop-shadow(0px 0px 2px ${colors.grey[900]}) drop-shadow(1px 1px 1px ${colors.grey[900]})`
                        : `drop-shadow(0px 0px 1px ${colors.grey[900]})`,
                      color: isDrafted
                        ? mutedGray
                        : favoritePlayerIds.includes(sleeper_id || id)
                        ? colors.redAccent[500]
                        : colors.grey[200],
                      strokeWidth: favoritePlayerIds.includes(sleeper_id || id) ? 1 : 0.5,
                      stroke: colors.grey[900],
                      fill: 'currentColor',
                      '& path': {
                        strokeWidth: 'inherit',
                        stroke: 'inherit',
                        fill: 'inherit',
                      },
                    },
                  }}
                >
                  <FavoriteIcon />
                </IconButton>

                {isKeeper && (
                  <LockPersonIcon
                    sx={{
                      backgroundColor: colors.primary[900],
                      color: isDrafted ? mutedGray : colors.greenAccent[400],
                      fontSize: {
                        xs: "0.9rem",
                        sm: "1rem",
                        md: "1rem",
                      },
                      borderRadius: "3px",
                      padding: "2px",
                      lineHeight: 1.0,
                      height: "fit-content",
                    }}
                  />
                )}
              </Box>
            {/* Top-right: round.pick and direction icon */}
            <Box
              sx={{
                position: "absolute",
                right: "2px",
                top: "2px",
                display: "flex",
                alignItems: "center",
                gap: 0.4,
                opacity: 0.6,
              }}
            >
              <Typography
                sx={{
                  color: isDrafted ? mutedGray : colors.grey[800],
                  fontSize: "10px",
                  fontWeight: "bold",
                  letterSpacing: "0.1px",
                  lineHeight: 1,
                }}
              >
                {round}.{draft_position}
              </Typography>
              <ArrowDirectionIcon
                round={round}
                draft_position={draft_position}
                teamCount={teamCount}
                color={isDrafted ? mutedGray : undefined}
              />
            </Box>

            {/* Player name */}
            <Typography
              variant="h6"
              sx={{
                color: textColor,
                fontSize: {
                  xs: "0.7rem",
                  sm: "0.75rem",
                  md: "0.8rem",
                },
                fontWeight: "bold",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                lineHeight: 1.2,
                flexShrink: 1,
                minWidth: 0,
                mb: 0.2,
                height: "16px",
                display: "block",
              }}
            >
              {formatPlayerName(name)}
            </Typography>

            {/* Team and Position with Tier - Combined for larger screens */}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 0.5,
                mb: 0.2,
                height: "14px",
              }}
            >
              <Typography
                sx={{
                  color: isDrafted ? mutedGray : colors.grey[800],
                  fontSize: {
                    xs: "0.55rem",
                    sm: "0.6rem",
                    md: "0.6rem",
                  },
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                  lineHeight: 1.0,
                }}
              >
                {`${position}${pos_rank || "?"}`} - {team || "FA"}({bye_week || "?"})
              </Typography>
              
            </Box>

            {/* ECR/ADP display with PlayerAvatar */}
            <Box
              display="flex"
              justifyContent="space-between"
              alignItems="flex-end"
              sx={{
                mt: "auto", // Push to bottom of remaining space
                mb: 0.2,
                position: "relative",
              }}
            >
              {/* ECR/ADP text stack */}
              <Box
                display="flex"
                flexDirection="column"
                gap={0.2}
                flex={1}
              >
                <Typography
                  sx={{
                    ...ecrStyle,
                    color: ecrStyle.color || (isDrafted ? mutedGray : textColor),
                    fontSize: {
                      xs: "0.55rem",
                      sm: "0.6rem",
                      md: "0.6rem",
                    },
                    lineHeight: 1.0,
                    width: "fit-content",
                  }}
                >
                  ECR {ovr_rank_ecr || "?"}
                </Typography>
                <Typography
                  sx={{
                    ...adpStyle,
                    color: adpStyle.color || (isDrafted ? mutedGray : textColor),
                    fontSize: {
                      xs: "0.55rem",
                      sm: "0.6rem",
                      md: "0.6rem",
                    },
                    lineHeight: 1.0,
                    width: "fit-content",
                  }}
                >
                  ADP {adp_rank === 999 ? "?" : adp_rank}
                </Typography>
              </Box>

              {/* PlayerAvatar positioned at bottom right */}
              <Box
                sx={{
                  position: "absolute",
                  bottom: 0,
                  right: 0,
                  display: { xs: "none", sm: "none", md: "none", lg: "none", xl: "block" },
                }}
              >
                <PlayerAvatar
                  sleeper_id={sleeper_id}
                  first_name={first_name}
                  last_name={last_name}
                  size={{
                    width: {
                      xl: "30px",
                    },
                    height: {
                      xl: "30px",
                    }
                  }}
                />
              </Box>
            </Box>
          </Box>

        {/* Context Menu */}
        <PlayerContextMenu
          anchorEl={contextMenuAnchor}
          open={Boolean(contextMenuAnchor)}
          onClose={handleCloseContextMenu}
          playerData={playerData}
          currentRound={round}
          currentPosition={draft_position}
        />
      </>
    );
  }
);

export default DraftGridCell;